export const Colors = {
  dark:         '#16233A',
  darkMid:      '#22324D',
  // ── App backgrounds ──────────────────────────────
  bg:           '#F7F5F0',
  surface:      '#FFFFFF',
  surfaceAlt:   '#F1EEE7',

  // ── Text hierarchy ───────────────────────────────
  text1:        '#16233A',
  text2:        '#56627A',
  text3:        '#667085',

  // ── Primary — teal ─────────────────────────────
  primary:      '#0B7A78',
  primaryLt:    '#E3F1EF',

  // ── Income — emerald ─────────────────────────────
  income:       '#12966A',
  incomeLt:     '#E2F3EC',
  incomeDk:     '#0B7A55',

  // ── Expense — rose ───────────────────────────────
  expense:      '#C23A50',
  expenseLt:    '#FBE8EB',
  expenseDk:    '#A92F44',

  // ── Warning — amber ──────────────────────────────
  warning:      '#B86E00',
  warningLt:    '#FBF0DC',
  warningDk:    '#8F5600',

  // Legacy card colors kept for old auth/card artwork only.
  cardGradA:    '#163B5B',
  cardGradB:    '#0B7A78',

  // ── Borders ──────────────────────────────────────
  border:       '#E7E3DA',
  borderCard:   'rgba(22,35,58,0.08)',

  // ── Tab bar ──────────────────────────────────────
  tabActive:    '#0B7A78',
  tabInactive:  '#667085',
  tabBg:        '#FFFFFF',

  // ── Backward compat (not to break components not yet updated) ──
  success:      '#12966A',
  successLight: '#E2F3EC',
  successDark:  '#0B7A55',
  error:        '#C23A50',
  errorLight:   '#FBE8EB',
  coral:        '#C23A50',
  violet:       '#4D57D8',
  blue:         '#4D57D8',
  blueDeep:     '#3540B8',
  blueBright:   '#6872E7',
  indigo:       '#4D57D8',
  indigoDark:   '#3540B8',
  purple:       '#4D57D8',
  violetDeep:   '#3540B8',
  textDark:     '#16233A',
  textLabel:    '#56627A',
  textCardMuted:'#667085',
  textCardSub:  '#56627A',
  textPrimary:  '#FFFFFF',
  textSecondary:'rgba(255,255,255,0.82)',
  textMuted:    'rgba(255,255,255,0.65)',
  bgGradientStart: '#1E1B4B',
  bgGradientMid:   '#3730A3',
  bgGradientEnd:   '#4F46E5',
  glass:           'rgba(255,255,255,0.13)',
  glassLine:       'rgba(255,255,255,0.3)',
  bgPanel:         '#FFFFFF',
  bgPanelDark:     '#F1EEE7',
  bgCard:          'rgba(255,255,255,0.15)',
};

export const Spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const Radius = {
  sm:   8,
  md:  12,
  lg:  16,
  xl:  20,
  full: 999,
};

export const FontSize = {
  xs:   11,
  sm:   13,
  md:   15,
  lg:   17,
  xl:   22,
  xxl:  28,
  xxxl: 36,
  hero: 48,
};

export const Shadow = {
  sm: {
    shadowColor: '#1E293B',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  md: {
    shadowColor: '#1E293B',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.09,
    shadowRadius: 16,
    elevation: 6,
  },
  lg: {
    shadowColor: '#1E1B4B',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 28,
    elevation: 12,
  },
};

// ── Typography — Plus Jakarta Sans ─────────────────
// Each weight is loaded as its own family (App.tsx). Text/TextInput from
// components/AppText pick the family from fontWeight automatically.
export const Fonts = {
  regular:   'PlusJakartaSans-Regular',
  medium:    'PlusJakartaSans-Medium',
  semibold:  'PlusJakartaSans-SemiBold',
  bold:      'PlusJakartaSans-Bold',
  extrabold: 'PlusJakartaSans-ExtraBold',
} as const;

export const FONT_SOURCES = {
  [Fonts.regular]:   require('../../assets/fonts/PlusJakartaSans_400Regular.ttf'),
  [Fonts.medium]:    require('../../assets/fonts/PlusJakartaSans_500Medium.ttf'),
  [Fonts.semibold]:  require('../../assets/fonts/PlusJakartaSans_600SemiBold.ttf'),
  [Fonts.bold]:      require('../../assets/fonts/PlusJakartaSans_700Bold.ttf'),
  [Fonts.extrabold]: require('../../assets/fonts/PlusJakartaSans_800ExtraBold.ttf'),
} as Record<string, number>;

export function fontFamilyForWeight(weight?: string | number | null): string {
  const value = weight === 'bold' ? 700 : weight === 'normal' || weight == null ? 400 : Number(weight);
  if (!Number.isFinite(value) || value < 500) return Fonts.regular;
  if (value < 600) return Fonts.medium;
  if (value < 700) return Fonts.semibold;
  if (value < 800) return Fonts.bold;
  return Fonts.extrabold;
}

/** Monetary values: fixed-width digits so amounts align in columns. */
export const Numeric = { fontVariant: ['tabular-nums'] as ('tabular-nums')[] };
